import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import cls from 'classnames'
import styles from './ImagePreview.module.less'

type ImagePreviewProps = {
  images: string[]
  initialIndex?: number
  open?: boolean
  aspectRatio?: number
  className?: string
  onClose?: () => void
  onIndexChange?: (index: number) => void
  maskClosable?: boolean
  showThumbnails?: boolean
}

const clampIndex = (index: number, total: number) => {
  if (total <= 0) return 0
  return Math.min(Math.max(index, 0), total - 1)
}

function ImagePreview({
  images,
  initialIndex = 0,
  open = true,
  aspectRatio = 16 / 9,
  className,
  onClose,
  onIndexChange,
  maskClosable = true,
  showThumbnails = true,
}: ImagePreviewProps) {
  const total = images.length
  const [currentIndex, setCurrentIndex] = useState(() => clampIndex(initialIndex, total))
  const thumbnailRefs = useRef<Array<HTMLButtonElement | null>>([])
  const panelStyle = useMemo(
    () => ({ '--preview-aspect': String(aspectRatio) }) as CSSProperties,
    [aspectRatio],
  )

  useEffect(() => {
    if (!open) return
    setCurrentIndex((prev) => clampIndex(prev, total))
  }, [open, total])

  useEffect(() => {
    if (!open) return
    setCurrentIndex(clampIndex(initialIndex, total))
  }, [initialIndex, open, total])

  useEffect(() => {
    if (!open) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.()
        return
      }
      if (event.key === 'ArrowLeft') {
        handlePrev()
        return
      }
      if (event.key === 'ArrowRight') {
        handleNext()
        return
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose, total, currentIndex])

  const currentImage = useMemo(() => images[currentIndex], [currentIndex, images])

  useEffect(() => {
    if (!open) return
    const currentThumb = thumbnailRefs.current[currentIndex]
    currentThumb?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [currentIndex, open])

  const updateIndex = (nextIndex: number) => {
    if (total === 0) return
    const clampedIndex = clampIndex(nextIndex, total)
    setCurrentIndex(clampedIndex)
    onIndexChange?.(clampedIndex)
  }

  const handlePrev = () => {
    if (total <= 1) return
    updateIndex((currentIndex - 1 + total) % total)
  }

  const handleNext = () => {
    if (total <= 1) return
    updateIndex((currentIndex + 1) % total)
  }

  if (!open) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className={styles.previewOverlay}
      onClick={() => {
        if (maskClosable) onClose?.()
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cls(styles.previewPanel, className)}
        style={panelStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.stage} style={{ aspectRatio }}>
          {currentImage ? (
            <img
              className={styles.previewImage}
              src={currentImage}
              alt=""
            />
          ) : (
            <div className={styles.emptyState}>No images</div>
          )}

          {total > 1 && (
            <>
              <button
                type="button"
                className={cls(styles.navButton, styles.prevButton)}
                onClick={handlePrev}
                aria-label="Previous image"
              >
                <svg viewBox="0 0 20 20" aria-hidden className={styles.navIcon}>
                  <path d="M12.5 5.5l-5 4.5 5 4.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                className={cls(styles.navButton, styles.nextButton)}
                onClick={handleNext}
                aria-label="Next image"
              >
                <svg viewBox="0 0 20 20" aria-hidden className={styles.navIcon}>
                  <path d="M7.5 5.5l5 4.5-5 4.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}
        </div>

        <div className={styles.counterRow}>
          <span className={styles.counter}>
            {total > 0 ? `${currentIndex + 1} / ${total}` : '0 / 0'}
          </span>
        </div>

        {showThumbnails && total > 1 && (
          <div className={styles.thumbnailRow}>
            {images.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                className={cls(styles.thumbnailButton, index === currentIndex && styles.thumbnailActive)}
                ref={(node) => {
                  thumbnailRefs.current[index] = node
                }}
                onClick={() => updateIndex(index)}
                aria-current={index === currentIndex ? 'true' : undefined}
              >
                <img className={styles.thumbnailImage} src={image} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export default ImagePreview
